import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';
import { Box, Button, TextField, Typography, Container, Paper, Divider } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/';

  // Check if already authenticated or if there's a stored redirect
  useEffect(() => {
    const storedRedirect = localStorage.getItem('loginRedirect');
    if (isAuthenticated) {
      const finalRedirect = storedRedirect || redirectUrl;
      if (storedRedirect) {
        localStorage.removeItem('loginRedirect');
      }
      navigate(finalRedirect, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectUrl, user?.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      return setError('Please fill in all fields');
    }

    try {
      setError('');
      setLoading(true);

      if (redirectUrl && redirectUrl !== '/') {
        localStorage.setItem('loginRedirect', redirectUrl);
      }

      const { error } = await signIn(email, password);
      if (error) throw error;

      const storedRedirect = localStorage.getItem('loginRedirect');
      const finalRedirect = storedRedirect || redirectUrl;
      if (storedRedirect) {
        localStorage.removeItem('loginRedirect');
      }
      navigate(finalRedirect);
    } catch (error) {
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Get the redirect URL (either from query params or localStorage)
      const redirectUrl = searchParams.get('redirect') || localStorage.getItem('loginRedirect') || '/';
      
      logger.debug('🔍 Google sign-in redirect URL:', redirectUrl);
      
      const { error } = await signInWithGoogle(redirectUrl);
      if (error) throw error;
      // The actual redirect happens in the Supabase client
    } catch (error) {
      setError(error.message || 'Failed to sign in with Google.');
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <svg width="52" height="52" viewBox="8 14 138 138" xmlns="http://www.w3.org/2000/svg" aria-label="Release Cadence">
            <defs>
              <clipPath id="login-body-clip">
                <path d="M48 18 L68 18 L88 100 L28 100 Z"/>
              </clipPath>
            </defs>
            <path d="M28 100 L88 100 L98 140 L18 140 Z" fill="#A87048"/>
            <path d="M48 18 L68 18 L88 100 L28 100 Z" fill="#241508"/>
            <rect x="54.5" y="22" width="7" height="78" fill="#EDE0C4" clipPath="url(#login-body-clip)"/>
            <path d="M48 18 L68 18 L98 140 L18 140 Z" fill="none" stroke="#7B5030" strokeWidth="1.7" strokeLinejoin="round"/>
            <rect x="21" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
            <rect x="50.5" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
            <rect x="80" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
            <line x1="58" y1="100" x2="135.8" y2="45.5" stroke="#9B7050" strokeWidth="2.6" strokeLinecap="round"/>
            <circle cx="58" cy="100" r="3.5" fill="#9B7050"/>
            <path d="M100.2 60.1 L110.0 74.1 L95.1 80.8 L88.8 71.8 Z" fill="#C07838" stroke="#7B5030" strokeWidth="1.4" strokeLinejoin="round"/>
          </svg>
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, letterSpacing: 0.5 }}>
            <span style={{ color: '#241508' }}>Release</span>
            <span style={{ color: '#7B5030' }}> Cadence</span>
          </Typography>
        </Box>
        <Typography component="h1" variant="h5" align="center" gutterBottom>
          Sign In
        </Typography>
        
        {error && (
          <Typography color="error" align="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/signup')}
          >
            Don't have an account? Sign Up
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;